import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { curriculumApi, Exercise } from '../../api/curriculumApi';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

// Import exercise components
import MatchPairsExercise from '../../components/curriculum/exercises/MatchPairsExercise';
import MultipleChoiceExercise from '../../components/curriculum/exercises/MultipleChoiceExercise';
import FillBlankExercise from '../../components/curriculum/exercises/FillBlankExercise';
import SentenceBuilderExercise from '../../components/curriculum/exercises/SentenceBuilderExercise';
import FlashcardExercise from '../../components/curriculum/exercises/FlashcardExercise';
import ListeningExercise from '../../components/curriculum/exercises/ListeningExercise';
import WordHuntExercise from '../../components/curriculum/exercises/WordHuntExercise';

/**
 * ExerciseSessionPage Component
 *
 * Displays and handles individual exercises.
 * Shows exercise content, collects answers, and provides feedback.
 */
const ExerciseSessionPage: React.FC = () => {
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const navigate = useNavigate();

  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userAnswer, setUserAnswer] = useState<any>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [feedback, setFeedback] = useState<string>('');

  useEffect(() => {
    if (exerciseId) {
      loadExercise(parseInt(exerciseId));
    }
  }, [exerciseId]);

  const loadExercise = async (id: number) => {
    try {
      setLoading(true);
      const exerciseData = await curriculumApi.getExercise(id);
      setExercise(exerciseData);
      setError(null);
    } catch (err) {
      console.error('Failed to load exercise:', err);
      setError('Failed to load exercise. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (answer: any) => {
    if (!exercise) return;

    try {
      const result = await curriculumApi.submitExercise(exercise.id, answer, 0);
      setIsCorrect(result.is_correct);
      setFeedback(result.feedback || (result.is_correct ? 'Correct! Well done!' : 'Not quite right. Try again!'));
      setShowFeedback(true);
      setUserAnswer(answer);
    } catch (err) {
      console.error('Failed to submit exercise:', err);
      setFeedback('Failed to submit answer. Please try again.');
      setShowFeedback(true);
    }
  };

  const handleContinue = () => {
    // Reset for next exercise or go back
    setShowFeedback(false);
    setUserAnswer(null);
    navigate(-1);
  };

  const handleSkip = () => {
    // Skip exercise and return to level
    navigate(-1);
  };

  const renderExercise = () => {
    if (!exercise) return null;

    switch (exercise.exercise_type) {
      case 'match_pairs':
        return (
          <MatchPairsExercise
            pairs={exercise.content.pairs || []}
            onSubmit={handleSubmit}
          />
        );

      case 'multiple_choice':
        return (
          <MultipleChoiceExercise
            questions={exercise.content.questions || []}
            onSubmit={handleSubmit}
          />
        );

      case 'fill_blank':
        return (
          <FillBlankExercise
            sentences={exercise.content.sentences || []}
            onSubmit={handleSubmit}
          />
        );

      case 'sentence_builder':
        return (
          <SentenceBuilderExercise
            items={exercise.content.items || []}
            onSubmit={handleSubmit}
          />
        );

      case 'flashcard':
        return (
          <FlashcardExercise
            cards={exercise.content.cards || []}
            onComplete={handleSubmit}
          />
        );

      case 'listening':
        return (
          <ListeningExercise
            items={exercise.content.items || []}
            onSubmit={handleSubmit}
          />
        );

      case 'word_hunt':
        return (
          <WordHuntExercise
            grid={exercise.content.grid || []}
            words={exercise.content.words || []}
            onSubmit={handleSubmit}
          />
        );

      default:
        return (
          <div className="card bg-base-200 shadow-lg">
            <div className="card-body">
              <p className="text-center text-base-content/70 py-8">
                Exercise renderer for "{exercise.exercise_type}" type is under development.
              </p>
              <div className="card-actions justify-center mt-6">
                <button onClick={handleBackClick} className="btn btn-primary">
                  Return to Level
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (error || !exercise) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100 p-4">
        <div className="alert alert-warning max-w-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 className="font-bold">Exercise System Coming Soon!</h3>
            <p className="text-sm">{error || 'Exercise not found'}</p>
            <button onClick={handleBackClick} className="btn btn-sm btn-ghost mt-2">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Placeholder exercise renderer
  return (
    <div className="min-h-screen bg-base-100 pb-20">
      {/* Header */}
      <div className="bg-base-200 py-6 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="flex justify-between items-start mb-4">
            <button
              onClick={handleBackClick}
              className="btn btn-ghost btn-sm"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </button>

            <button
              onClick={handleSkip}
              className="btn btn-outline btn-sm"
            >
              Skip Exercise
            </button>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-base-content">
            {exercise.title}
          </h1>
          <p className="text-base-content/70 mt-2">
            {exercise.instructions}
          </p>
          <div className="flex gap-3 mt-3">
            <div className="badge badge-primary">{exercise.exercise_type}</div>
            <div className="badge badge-ghost">⭐ {exercise.xp_reward} XP</div>
          </div>
        </div>
      </div>

      {/* Exercise Content */}
      <div className="container mx-auto max-w-3xl px-4 py-8">
        {renderExercise()}
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className={`font-bold text-lg ${isCorrect ? 'text-success' : 'text-error'}`}>
              {isCorrect ? '🎉 Correct!' : '❌ Not Quite'}
            </h3>
            <p className="py-4">{feedback}</p>
            <div className="modal-action">
              <button onClick={handleContinue} className="btn btn-primary">
                {isCorrect ? 'Continue' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseSessionPage;
